import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { useWebAuth } from '../contexts/WebAuthContext';

/**
 * Webログインページ
 *
 * ダウンロードページなどの保護されたページにアクセスするための
 * Webブラウザ用ログインページ
 */
export function WebLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, error: authError, signIn: authSignIn } = useWebAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // リダイレクト先URL
  const redirectUrl = searchParams.get('redirect') ?? '/download';

  // 既に認証済みの場合はリダイレクト
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(decodeURIComponent(redirectUrl), { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, redirectUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await authSignIn(email, password);
      // 成功した場合、useEffectでリダイレクトされる
    } catch (err) {
      const message = err instanceof Error ? err.message : '認証に失敗しました';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // 初期ローディング中
  if (isLoading) {
    return (
      <AuthLayout title="ログイン" subtitle="認証を確認中...">
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-muted border-l-primary-500 rounded-full animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  const displayError = error || authError;

  return (
    <AuthLayout title="ログイン" subtitle="ダウンロードページにアクセスするにはログインが必要です">
      <form onSubmit={handleSubmit} className="space-y-6">
        {displayError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <p className="text-red-400 text-sm">{displayError}</p>
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            メールアドレス
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="example@email.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
            パスワード
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="パスワードを入力"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'ログイン中...' : 'ログイン'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-muted-foreground">
          アカウントをお持ちでない方は
          <a
            href={`/signup?redirect=${encodeURIComponent(redirectUrl)}`}
            className="text-primary-400 hover:text-primary-300 transition-colors ml-1"
          >
            アカウント作成
          </a>
        </p>
      </div>
    </AuthLayout>
  );
}
